/* eslint-disable */
// NotificationAttachments — the attachments of ONE notification, resolved in
// the reader's own session and rendered at the surface's density.
//
// ⚠️ PRECEDENCE (spec §2.3). `notifications.entity_type` is copied from the
// TYPE at send time, so a system.announcement carrying an attached model has
// entity_type = 'none' on the row while payload.attachments says 'item'. Those
// answer different questions and neither is wrong. The rule is one line:
//   attachments present and non-empty  -> use them, ignore entity_type entirely
//   otherwise                          -> the caller's existing entity_type path
// Every notification sent before P6 takes the second branch and renders exactly
// as it did before. This component simply returns null when there is nothing to
// show, which is what leaves the old path untouched.

import React, { useMemo } from 'react';
import { readAttachments, legacyAttachment } from '../../utils/attachmentRegistry';
import { useAttachmentEntities } from '../../hooks/useAttachmentEntities';
import AttachmentCard from './AttachmentCard';
import styles from './attachments.module.css';

export function NotificationAttachments({ payload, entityType, entityId, onNavigate }) {
  // §2.3 precedence, now with the legacy pair as the fallback rather than
  // nothing. attachments win outright; with none, entity_type + entity_id
  // render ONE attachment when that type maps to a registry family. A
  // notification with payload {} and entity_type 'none' still renders nothing,
  // which is what keeps every pre-P6 message identical.
  const refs = useMemo(() => {
    const fromPayload = readAttachments(payload);
    if (fromPayload.length) return fromPayload;
    const legacy = legacyAttachment(entityType, entityId);
    return legacy ? [legacy] : [];
  }, [payload, entityType, entityId]);
  const { byKey, state } = useAttachmentEntities(refs);

  if (!refs.length) return null;

  return (
    // The block shares the card's horizontal padding — nothing touches the
    // frame. David's words were that the picture was "stuck to the left frame";
    // the list had no inset, so it ran to the edge while the text above did not.
    <div className={styles.block} data-test="notification-attachments">
      <div className={styles.blockLabel}>
        ATTACHED<span className={styles.blockCount}>· {refs.length}</span>
      </div>
      {refs.map((r) => {
        const key = r.kind + '|' + r.id + (r.byEntityId ? '|e' : '');
        // undefined = still resolving (neutral tile at final size)
        // null      = asked and got nothing: RLS hid it, or it is gone
        const entity = state === 'ready' ? (byKey[key] ?? null) : byKey[key];
        return (
          <AttachmentCard
            key={key}
            entity={entity}
            refKind={r.kind}
            interactive
            onNavigate={onNavigate}
          />
        );
      })}
    </div>
  );
}

export default NotificationAttachments;
